import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AddIcon from '@mui/icons-material/Add';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import colors from 'assets/theme/base/colors';

import {
  conversationalChat,
  getSessionHistory,
  listSessions,
  pinSession,
  submitTurnFeedback,
} from '../../service/service';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import FeedbackWidget, { isFeedbackDone } from './FeedbackWidget';
import './PepWiseAIChatPopup.scss';

import { Message, PromptChip, SemanticResult, PepWiseAIChatPopupProps } from './PepWiseAIChatPopup.types';
import {
  resolveUserId,
  newSessionId,
  sessionKey,
  msgCacheKey,
  getOrCreateSessionId,
  readCachedMessages,
  clearChatStorage,
  formatAssistantMessage,
} from './PepWiseAIChatPopup.utils';
import MessageBubble, { TypingBubble } from './MessageBubble';
import PromptChips from './PromptChips';
import ChatInputBar from './ChatInputBar';
import SidebarGroup from './SidebarGroup';

export type { PepWiseAIChatPopupProps };
export { clearChatStorage };


// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const PepWiseAIChatPopup: React.FC<PepWiseAIChatPopupProps> = ({
  onClose,
  suggestedPrompts = [],
  initialMessages = [],
}) => {
  const [authController] = usePepwiseAuthController();
  const { accessToken } = authController;

  // ── Step 1: resolve userId BEFORE any useState that needs it ──────────────
  // We use a ref so the value is stable for the component's lifetime.
  // getDecodedToken() is cheap (just reads a cookie), so calling it once is fine.
  const userIdRef = useRef<string>(resolveUserId());
  const userId = userIdRef.current;

  // ── Step 2: resolve sessionId BEFORE messages state ───────────────────────
  // Stored in a ref as well so async handlers always get the latest value
  // without needing to be recreated (avoids stale closure bugs).
  const sessionIdRef = useRef<string>(getOrCreateSessionId(userId));

  // ── Step 3: all useState calls — userId and sessionId are already valid ───
  const [sessionId, setSessionId] = useState<string>(sessionIdRef.current);

  const [messages, setMessages] = useState<Message[]>(() => {
    // Read from sessionStorage cache for instant display while history loads
    const cached = readCachedMessages(sessionIdRef.current);
    return cached ?? initialMessages;
  });

  const [isExpanded, setIsExpanded] = useState<boolean>(() =>
    sessionStorage.getItem('pepwise_chat_isExpanded') === 'true'
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);

  const [inputValue, setInputValue] = useState('');
  const [dynamicChips, setDynamicChips] = useState<PromptChip[]>([]);
  const [chipsTitle, setChipsTitle] = useState('Suggested follow-ups');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [sessionsList, setSessionsList] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Ref mirror of messages — always current, avoids stale closure in callbacks
  const messagesRef = useRef<Message[]>(messages);

  // ── Feedback widget state (AC1, AC2, AC10, AC12) ──────────────────────────
  const [showFeedbackWidget, setShowFeedbackWidget] = useState(false);

  /**
   * Determine whether this session qualifies for feedback collection:
   * - AC12: at least one successful assistant response (non-error, non-empty)
   * - AC10: feedback not already submitted/skipped this session
   */
  const sessionHasSuccessfulResponse = useCallback(
    (msgs: Message[]) =>
      msgs.some(
        (m) =>
          m.role === 'assistant' &&
          m.content.trim() !== '' &&
          m.content !== 'Thinking...' &&
          !m.content.toLowerCase().startsWith('sorry, i encountered')
      ),
    []
  );

  /**
   * Wraps onClose — shows the feedback widget when appropriate (AC1).
   * Called by all Close buttons in both compact and expanded views.
   */
  const handleCloseWithFeedback = useCallback(() => {
    const sid = sessionIdRef.current;
    // Use messagesRef.current instead of the messages closure to avoid stale
    // state after a "New Chat" (sessionIdRef is updated synchronously but
    // messages state is still pending a React re-render at that point).
    if (
      !isFeedbackDone(sid) &&
      messagesRef.current.length > initialMessages.length &&
      sessionHasSuccessfulResponse(messagesRef.current)
    ) {
      setShowFeedbackWidget(true);
    } else {
      onClose();
    }
  }, [onClose, sessionHasSuccessfulResponse, initialMessages.length]);

  /** Called by FeedbackWidget after Submit or Skip — exit the chat. */
  const handleFeedbackDone = useCallback(() => {
    setShowFeedbackWidget(false);
    onClose();
  }, [onClose]);

  // Keep sessionIdRef in sync whenever state changes
  useEffect(() => {
    sessionIdRef.current = sessionId;
    // Reset feedback widget visibility on session change
    setShowFeedbackWidget(false);
  }, [sessionId]);

  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ── Load history from Cosmos whenever the active session changes ──────────
  useEffect(() => {
    if (!sessionId) return;

    setIsHistoryLoading(true);
    setShowSuggestions(false); // hide panel when switching sessions
    // don't clear messages! we rely on cache for instant display while switching

    getSessionHistory(sessionId)
      .then((turns) => {
        if (!turns || turns.length === 0) return;
        console.log('HISTORY API RESPONSE:', turns);
        const fmt = (ts: string) =>
          new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        const restored: Message[] = [];
        turns.forEach((turn: any, idx: number) => {
          let parsedSemanticResult = turn.semantic_result || turn.semanticResult || turn.semantic_data || turn.metadata || turn.payload;
          if (typeof parsedSemanticResult === 'string') {
            try { parsedSemanticResult = JSON.parse(parsedSemanticResult); } catch { }
          }

          const turnDurationMs = turn.response_time || turn.response_time_ms || turn.duration_ms || turn.execution_ms || turn.latency_ms;
          let restoredRespTime: string | undefined = undefined;
          if (typeof turnDurationMs === 'number') {
            restoredRespTime = turnDurationMs >= 1000 ? `${(turnDurationMs / 1000).toFixed(1)}s` : `${turnDurationMs}ms`;
          } else if (typeof turnDurationMs === 'string') {
            restoredRespTime = turnDurationMs;
          }

          restored.push(
            { id: idx * 2 + 1, role: 'user', content: turn.user_message, time: fmt(turn.timestamp) },
            { id: idx * 2 + 2, role: 'assistant', content: formatAssistantMessage(turn.assistant_message), time: fmt(turn.timestamp), responseTime: restoredRespTime, semanticResult: parsedSemanticResult || undefined, turnId: turn.turn_id, feedback: turn.feedback }
          );
        });
        const newMessages = [...initialMessages, ...restored];
        setMessages((prev) => {
          // Only overwrite if the new messages length is different. 
          // This preserves the rich semanticResult cached in sessionStorage!
          if (prev.length > initialMessages.length && prev.length === newMessages.length) {
            return prev;
          }
          return newMessages;
        });
        // Seed chips from last assistant message in restored history
        const lastTurn = turns.at(-1);
        if (lastTurn) {
          try {
            const rawMsg = typeof lastTurn.assistant_message === 'string' ? JSON.parse(lastTurn.assistant_message) : lastTurn.assistant_message;
            const clarifications = rawMsg?.clarification_options || lastTurn.clarification_options || [];
            const followUps = rawMsg?.follow_up_suggestions || lastTurn.follow_up_suggestions || [];
            if (clarifications.length > 0) {
              setDynamicChips(clarifications.map((label: string) => ({ label })));
              setChipsTitle('Clarification Questions');
            } else if (followUps.length > 0) {
              setDynamicChips(followUps.map((label: string) => ({ label })));
              setChipsTitle('Suggested follow-ups');
            } else {
              setDynamicChips([]);
              setChipsTitle('');
            }
          } catch {
            setDynamicChips([]);
            setChipsTitle('');
          }
        }
      })
      .catch(() => { /* history load failure is non-fatal */ })
      .finally(() => setIsHistoryLoading(false));
  }, [sessionId]); // intentionally omitting other deps to run only on sessionId change

  // ── Load sessions list for the sidebar in expanded mode ───────────────────
  const refreshSessionsList = useCallback(() => {
    listSessions(30)
      .then((s) => setSessionsList(s || []))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (isExpanded) refreshSessionsList();
  }, [isExpanded, refreshSessionsList]);

  // ── Clear all state when user logs out ────────────────────────────────────
  useEffect(() => {
    if (!accessToken) {
      clearChatStorage(userId);
      setMessages(initialMessages);
      setIsExpanded(false);
      setInputValue('');
    }
  }, [accessToken]); // intentionally omitting other deps — only react to token change

  // ── Scroll to bottom + write message cache on every messages change ───────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (messages.length > initialMessages.length) {
      try {
        sessionStorage.setItem(msgCacheKey(sessionIdRef.current), JSON.stringify(messages));
      } catch { /* quota exceeded — ignore */ }
    }
  }, [messages]); // intentionally omitting other deps — only react to messages change

  // ── New Chat: start fresh without deleting the active session ───────────────
  const handleNewChat = useCallback(() => {
    const nextId = newSessionId();
    localStorage.setItem(sessionKey(userId), nextId);
    sessionIdRef.current = nextId;
    setSessionId(nextId);

    setMessages(initialMessages);
    setInputValue('');
    setIsLoading(false);

    refreshSessionsList();
  }, [userId, initialMessages, refreshSessionsList]);

  // ── Switch to a different session from the sidebar ────────────────────────
  const handleSelectSession = useCallback((selectedId: string) => {
    if (selectedId === sessionIdRef.current) return;
    localStorage.setItem(sessionKey(userId), selectedId);
    sessionIdRef.current = selectedId;
    setSessionId(selectedId);
    setMessages(readCachedMessages(selectedId) ?? initialMessages);
    setInputValue('');
  }, [userId, initialMessages]);

  const toggleExpanded = useCallback((val: boolean) => {
    setIsExpanded(val);
    sessionStorage.setItem('pepwise_chat_isExpanded', String(val));
  }, []);

  const updateMessage = useCallback((msgId: number, updates: Partial<Message>) => {
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, ...updates } : m)));
  }, []);

  const handleTurnFeedback = useCallback(async (msgId: number, feedback: 'up' | 'down') => {
    const msg = messagesRef.current.find(m => m.id === msgId);
    if (!msg || !msg.turnId) return;

    // Toggle off if clicking the same feedback again
    const newFeedback = msg.feedback === feedback ? null : feedback;
    updateMessage(msgId, { feedback: newFeedback });

    try {
      await submitTurnFeedback(sessionIdRef.current, msg.turnId, newFeedback);
    } catch (e) {
      // Revert if API fails
      updateMessage(msgId, { feedback: msg.feedback });
    }
  }, [updateMessage]);

  // ── Send a message ────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    setShowSuggestions(false);

    // Capture the current session at call time to avoid stale closures
    const activeSessionId = sessionIdRef.current;
    const assistantMsgId = Date.now();
    const now = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

    const startTime = Date.now();

    // Add user message + assistant placeholder
    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, role: 'user', content: text, time: now },
      { id: assistantMsgId, role: 'assistant', content: 'Thinking...' },
    ]);
    setInputValue('');
    setIsLoading(true);

    // Reveal text one word at a time for a streaming feel (non-streaming path)
    const revealGradually = async (fullText: string) => {
      const chunks = fullText.split(/(\s+)/);
      let built = '';
      for (const chunk of chunks) {
        built += chunk;
        updateMessage(assistantMsgId, { content: built });
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 14));
      }
    };

    try {
      let streamed = '';
      let streamStarted = false;
      let capturedSemanticResult: any = null;

      const response = await conversationalChat(
        activeSessionId,
        {
          chatId: activeSessionId,
          messages: [{ role: 'user', content: [{ type: 'text', text }] }],
        },
        {
          onChunk: (chunk: string) => {
            if (!streamStarted) {
              streamStarted = true;
              setIsLoading(false);
              streamed = '';
            }
            streamed += chunk;
            updateMessage(assistantMsgId, { content: formatAssistantMessage(streamed) });
          },
          onSemanticResult: (result: SemanticResult) => {
            capturedSemanticResult = result;
            updateMessage(assistantMsgId, { semanticResult: result });
          },
        }
      );

      const doneAt = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      const durationMs = Date.now() - startTime;
      const responseTimeStr = durationMs >= 1000 ? `${(durationMs / 1000).toFixed(1)}s` : `${durationMs}ms`;

      const raw = streamed || response?.answer || response?.message || response?.response || (typeof response === 'string' ? response : JSON.stringify(response));
      const finalText = formatAssistantMessage(raw || 'No response returned.');

      if (!streamStarted) {
        setIsLoading(false);
        await revealGradually(finalText);
      }

      // Fetch history to get the backend-generated turn_id for feedback
      try {
        const history = await getSessionHistory(activeSessionId);
        if (history && history.length > 0) {
          const lastTurn = history[history.length - 1];
          if (lastTurn && lastTurn.turn_id) {
            updateMessage(assistantMsgId, { turnId: lastTurn.turn_id });
          }
        }
      } catch (e) {
        console.error("Failed to fetch history for turnId", e);
      }

      // Stamp final content + timestamp + response time on the assistant bubble
      updateMessage(assistantMsgId, { content: finalText, time: doneAt, responseTime: responseTimeStr });
      // Update suggestion chips based on response content and reveal panel
      let nextChips: PromptChip[] = [];
      let nextTitle = '';
      const dataObj = capturedSemanticResult || (typeof response === 'object' ? response : null);
      if (dataObj) {
        const clarifications = dataObj.clarification_options || [];
        const followUps = dataObj.follow_up_suggestions || [];
        if (clarifications.length > 0) {
          nextChips = clarifications.map((label: string) => ({ label }));
          nextTitle = 'Clarification Questions';
        } else if (followUps.length > 0) {
          nextChips = followUps.map((label: string) => ({ label }));
          nextTitle = 'Suggested follow-ups';
        }
      }
      setDynamicChips(nextChips);
      setChipsTitle(nextTitle);
      setShowSuggestions(true);
    } catch {
      const errAt = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      updateMessage(assistantMsgId, { content: 'Sorry, I encountered an error. Please try again.', time: errAt });
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, updateMessage]);

  const handlePromptSelect = useCallback((label: string) => {
    setInputValue(label);
    setShowSuggestions(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Shared styles
  // ─────────────────────────────────────────────────────────────────────────

  const headerIconBtnSx = {
    color: '#fff', p: '4px',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  };

  // ─────────────────────────────────────────────────────────────────────────
  // EXPANDED (full-screen) view
  // ─────────────────────────────────────────────────────────────────────────

  if (isExpanded) {


    return (
      <Box sx={{ position: 'fixed', top: '40px', left: '60px', right: 0, bottom: 0, backgroundColor: colors.backgroundLight.secondary, display: 'flex', flexDirection: 'column', zIndex: 1200, boxShadow: '0 0 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1, backgroundColor: colors.landingPageTile.background, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isSidebarOpen && (
              <Tooltip title="Open sidebar">
                <IconButton size="small" onClick={() => setIsSidebarOpen(true)} sx={{ color: '#fff', p: '4px', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}>
                  <ViewSidebarOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="dashboardTileTitle" sx={{ color: '#ffffff', fontSize: '14px !important', lineHeight: '1 !important' }}>
              Conversational Agent
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '2px' }}>
            <Tooltip title="New Chat">
              <IconButton size="small" onClick={handleNewChat} sx={headerIconBtnSx} disabled={isLoading || isHistoryLoading}>
                <AddIcon style={{ width: 18, height: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Compact view">
              <IconButton size="small" onClick={() => toggleExpanded(false)} sx={headerIconBtnSx}>
                <CloseFullscreenIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={handleCloseWithFeedback} sx={headerIconBtnSx}>
                <CloseIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Body: sidebar + chat */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Sidebar */}
          {isSidebarOpen && (
            <Box sx={{ width: '240px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mx: 1, mt: 2, mb: 1.5, gap: 1 }}>
                <Box onClick={handleNewChat} sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, px: 1.5, py: 1.25, borderRadius: '8px', cursor: 'pointer', backgroundColor: '#003771', color: '#FFFFFF', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,55,113,0.15)', '&:hover': { backgroundColor: '#002B5E', transform: 'translateY(-1px)', boxShadow: '0 4px 6px rgba(0,55,113,0.2)' } }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>New Chat</Typography>
                </Box>
                <Tooltip title="Close sidebar">
                  <IconButton onClick={() => setIsSidebarOpen(false)} sx={{ color: '#003771', border: '1px solid #D0DFF0', backgroundColor: '#F0F5FB', borderRadius: '8px', p: '8px', '&:hover': { backgroundColor: '#E2E8F0' } }}>
                    <ViewSidebarOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box className="pepwise-scrollbar" sx={{ flex: 1, overflowY: 'auto', pb: 1 }}>
                {sessionsList.length === 0
                  ? <Typography sx={{ px: 1.5, pt: 2, fontSize: '11px', color: '#94A3B8', textAlign: 'center' }}>No previous sessions</Typography>
                  : <>
                    <SidebarGroup label="Pinned" items={sessionsList.filter(s => s.is_pinned)} sessionId={sessionId} onSelectSession={handleSelectSession} pinnedIds={sessionsList.filter(s => s.is_pinned).map(s => s.session_id)} onTogglePin={(id, e) => {
                      e.stopPropagation();
                      setSessionsList(prev => prev.map(s => s.session_id === id ? { ...s, is_pinned: false } : s));
                      pinSession(id, false).catch(() => refreshSessionsList());
                    }} />
                    <SidebarGroup label="Recent" items={sessionsList.filter(s => !s.is_pinned)} sessionId={sessionId} onSelectSession={handleSelectSession} pinnedIds={sessionsList.filter(s => s.is_pinned).map(s => s.session_id)} onTogglePin={(id, e) => {
                      e.stopPropagation();
                      setSessionsList(prev => prev.map(s => s.session_id === id ? { ...s, is_pinned: true } : s));
                      pinSession(id, true).catch(() => refreshSessionsList());
                    }} />
                  </>
                }
              </Box>
            </Box>
          )}

          {/* Chat area */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            <Box className="pepwise-scrollbar" sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column' }}>
              {messages.map((m, i) => (
                <MessageBubble key={m.id} msg={m} isStreaming={isLoading && i === messages.length - 1} onFeedback={handleTurnFeedback} />
              ))}
              {isLoading && <TypingBubble />}
              {isHistoryLoading && messages.length <= initialMessages.length && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, color: '#94A3B8' }}>
                  <CircularProgress size={12} sx={{ color: '#94A3B8' }} />
                  <Typography sx={{ fontSize: '11px' }}>Loading history...</Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
            <PromptChips prompts={dynamicChips} onSelect={handlePromptSelect} visible={showSuggestions} title={chipsTitle} />
            <ChatInputBar value={inputValue} onChange={setInputValue} onSend={handleSend} isLoading={isLoading} />
            {/* Feedback widget overlay — slide-up panel (AC1, AC2) */}
            {showFeedbackWidget && (
              <FeedbackWidget
                sessionId={sessionId}
                onDone={handleFeedbackDone}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // COMPACT (popup) view
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ position: 'fixed', bottom: '76px', right: '20px', width: '597px', height: '80vh', minHeight: '400px', maxHeight: '915px', backgroundColor: colors.backgroundLight.secondary, borderRadius: '10px', boxShadow: `0 8px 36px rgba(0,0,0,0.2), 0 0 0 2px ${colors.landingPageTile.background}`, display: 'flex', flexDirection: 'column', zIndex: 1200, overflow: 'hidden', animation: 'pepwiseAISlideUp 0.22s ease-out' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px', px: 1.5, py: 0.75, backgroundColor: colors.landingPageTile.background, borderRadius: '10px 10px 0 0' }}>
        <Tooltip title="New Chat">
          <IconButton size="small" onClick={handleNewChat} sx={headerIconBtnSx} disabled={isLoading || isHistoryLoading}>
            <AddIcon style={{ width: 18, height: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Expand">
          <IconButton size="small" onClick={() => toggleExpanded(true)} sx={headerIconBtnSx}>
            <OpenInFullIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={handleCloseWithFeedback} sx={headerIconBtnSx}>
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Messages */}
      <Box className="pepwise-scrollbar" sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column' }}>
        {messages.map((m, i) => (
          <MessageBubble key={m.id} msg={m} isStreaming={isLoading && i === messages.length - 1} onFeedback={handleTurnFeedback} />
        ))}
        {isLoading && <TypingBubble />}
        <div ref={messagesEndRef} />
      </Box>

      <PromptChips prompts={dynamicChips} onSelect={handlePromptSelect} visible={showSuggestions} title={chipsTitle} />
      <ChatInputBar value={inputValue} onChange={setInputValue} onSend={handleSend} isLoading={isLoading} />

      {/* Feedback widget overlay — slide-up panel (AC1, AC2) */}
      {showFeedbackWidget && (
        <FeedbackWidget
          sessionId={sessionId}
          onDone={handleFeedbackDone}
        />
      )}
    </Box>
  );
};

export default PepWiseAIChatPopup;
